/**
 * @file Enemy archetypes (GDD §Fase 3 — nuevos enemigos).
 *
 * Same contract as the weapon table: an archetype is a data row, never a
 * subclass. `createEnemy(ctx, x, z, 'brute')` composes the components listed
 * here, the prefab registry builds one shared mesh per row, and the wave
 * director costs them out of its budget.
 */

import { PALETTE } from './Palette.js';

/**
 * @typedef {object} EnemyArchetype
 * @property {string} name Display name.
 * @property {'melee' | 'ranged' | 'ranged_template'} behavior Which AI branch the brain takes.
 *   `ranged_template` uses the `enemy_ranged_01` FSM ({@link ./EnemyRanged.js}) instead of AIBrain.
 * @property {number} radius Collider and mesh radius.
 * @property {number} height Mesh height, used for the render Y offset.
 * @property {number} maxHealth Hit points.
 * @property {number} speed Top speed in units/second.
 * @property {number} acceleration Steering rate.
 * @property {number} damage Contact damage (melee only).
 * @property {number} attackRate Attacks per second (melee only).
 * @property {number} attackRange Distance at which it commits.
 * @property {number} seekRange Distance at which it starts chasing.
 * @property {number} preferredRange Stand-off distance (ranged only).
 * @property {number} separationRadius Personal space kept from other agents.
 * @property {number} separationStrength Push strength inside that radius.
 * @property {number} scoreValue Score awarded on death.
 * @property {number} color Body colour.
 * @property {string} [weapon] Key into the weapon table (ranged only).
 * @property {{ radius: number, damage: number }} [explosive] Death blast, if any.
 * @property {number} [dropBonus] Extra powerup drop chance, 0..1.
 * @property {Partial<import('./EnemyRanged.js').RangedTemplateConfig>} [ranged]
 *   Parameter overrides for the `enemy_ranged_01` template (`behavior: 'ranged_template'`).
 * @property {string} [starterState] Initial FSM state (see {@link ../components/AIBrain.js AIState}).
 * @property {import('../components/AIBrain.js').DetectionConfig} [detection] Vision-cone tuning.
 * @property {import('../components/AIBrain.js').AggroConfig} [aggro] Charge/pursuit tuning.
 * @property {import('../components/AIBrain.js').IdleMoveConfig} [idle] Idle-movement tuning.
 * @property {ExplosiveTriggerConfig} [explosiveTrigger] Proximity blast (suicide bomber).
 * @property {number} [despawnSeconds] Seconds a corpse lingers before cleanup.
 */

/**
 * @typedef {object} ExplosiveTriggerConfig
 * @property {number} [triggerDistance] Proximity that arms detonation.
 * @property {number} [fuseSeconds] Delay from trigger to blast.
 * @property {number} [deathFuseSeconds] Delay when detonating on death.
 * @property {boolean} [explodeOnDeath] Blow up if killed before contact.
 * @property {number} [damage] Damage at the centre.
 * @property {number} [radius] Blast radius.
 * @property {boolean} [falloffEnabled] Scale damage down with distance.
 * @property {number} [minimumDamage] Damage at the rim when falloff is on.
 * @property {number} [force] Knockback impulse applied to victims.
 * @property {boolean} [requiresLineOfSight] Walls shield victims when true.
 * @property {boolean} [affectsPlayer] Whether the blast hurts the player.
 * @property {boolean} [affectsEnemies] Whether it hurts other enemies (chains).
 * @property {boolean} [affectsEnvironment] Whether it damages destructibles.
 */

/** @type {Readonly<Record<string, EnemyArchetype>>} */
export const ENEMIES = Object.freeze({
  grunt: {
    name: 'Grunt',
    behavior: 'melee',
    radius: 0.68,
    height: 1.5,
    maxHealth: 10,
    speed: 2.5,
    acceleration: 26,
    damage: 22,
    attackRate: 1.1,
    attackRange: 1.7,
    seekRange: 74,
    preferredRange: 0,
    separationRadius: 1.9,
    separationStrength: 9,
    scoreValue: 10,
    color: PALETTE.enemyGrunt,
    starterState: 'idle_roaming',
    detection: { range: 16, angle: 150, checkSeconds: 0.2 },
    idle: { walkSpeed: 2.5, roamingSelectSeconds: 2.6, roamingRange: 6 },
    alertRadius: 9,
    retreatHealthRatio: 0.3
  },

  runner: {
    name: 'Runner',
    behavior: 'melee',
    radius: 0.48,
    height: 1.1,
    maxHealth: 10,
    speed: 6,
    acceleration: 44,
    damage: 14,
    attackRate: 1.8,
    attackRange: 1.4,
    seekRange: 90,
    preferredRange: 0,
    separationRadius: 1.3,
    separationStrength: 7,
    scoreValue: 15,
    color: PALETTE.enemyRunner,
    starterState: 'idle_patrol',
    detection: { range: 18, angle: 160, checkSeconds: 0.18 },
    idle: { walkSpeed: 6, patrolSelectSeconds: 2.2, patrolRange: 9, patrolMaxDistanceFromSpawn: 22 },
    alertRadius: 7
  },

  brute: {
    name: 'Brute',
    behavior: 'melee',
    radius: 1.35,
    height: 2.9,
    maxHealth: 30,
    speed: 1.5,
    acceleration: 12,
    damage: 52,
    attackRate: 0.7,
    attackRange: 2.7,
    seekRange: 74,
    preferredRange: 0,
    separationRadius: 3.2,
    separationStrength: 12,
    scoreValue: 60,
    color: PALETTE.enemyBrute,
    dropBonus: 0.35,
    starterState: 'idle_static',
    detection: { range: 14, angle: 120, checkSeconds: 0.25 },
    alertRadius: 12,
    leader: true
  },

  spitter: {
    name: 'Spitter',
    behavior: 'ranged',
    radius: 0.62,
    height: 1.4,
    maxHealth: 10,
    speed: 2.5,
    acceleration: 22,
    damage: 0,
    attackRate: 0,
    // Opens fire from here, and backs off if the player closes inside it.
    attackRange: 22,
    seekRange: 80,
    preferredRange: 14,
    separationRadius: 2.4,
    separationStrength: 10,
    scoreValue: 25,
    color: PALETTE.enemySpitter,
    weapon: 'spitter',
    starterState: 'idle_patrol',
    detection: { range: 18, angle: 130, checkSeconds: 0.2 },
    idle: { walkSpeed: 2.5, patrolSelectSeconds: 2.6, patrolRange: 8 },
    alertRadius: 8
  },

  /**
   * Short-range flamethrower foe (issue #14). Holds mid-close and hoses the
   * player with the Flamethrower; AI reuses the ranged branch with a tighter preferred
   * range so the cone actually connects.
   */
  burner: {
    name: 'Burner',
    behavior: 'ranged',
    radius: 0.7,
    height: 1.55,
    maxHealth: 10,
    speed: 2.5,
    acceleration: 20,
    damage: 0,
    attackRate: 0,
    attackRange: 11,
    seekRange: 72,
    preferredRange: 7,
    separationRadius: 2.6,
    separationStrength: 10,
    scoreValue: 30,
    color: PALETTE.enemyBurner,
    weapon: 'flamethrower_enemy',
    starterState: 'idle_roaming',
    detection: { range: 12, angle: 130, checkSeconds: 0.2 },
    idle: { walkSpeed: 2.5, roamingSelectSeconds: 2.8, roamingRange: 5 },
    alertRadius: 7
  },

  exploder: {
    name: 'Exploder',
    behavior: 'melee',
    radius: 0.75,
    height: 1.5,
    maxHealth: 10,
    speed: 2.5,
    acceleration: 30,
    // Deals no contact damage: the whole threat is the blast it leaves behind.
    damage: 0,
    attackRate: 1,
    attackRange: 1.9,
    seekRange: 84,
    preferredRange: 0,
    separationRadius: 2.2,
    separationStrength: 8,
    scoreValue: 20,
    color: PALETTE.enemyExploder,
    explosive: { radius: 5.2, damage: 68 },
    starterState: 'idle_roaming',
    detection: { range: 14, angle: 140, checkSeconds: 0.22 },
    idle: { walkSpeed: 2.5, roamingSelectSeconds: 3, roamingRange: 5 },
    alertRadius: 8
  },

  /**
   * Base `enemy_ranged_01` template. New ranged variants are made by copying
   * this row and changing the `ranged` parameter block — never by subclassing.
   */
  enemy_ranged_01: {
    name: 'Sentinel',
    behavior: 'ranged_template',
    radius: 0.64,
    height: 1.55,
    maxHealth: 10,
    speed: 2.5,
    acceleration: 24,
    damage: 0,
    attackRate: 0,
    attackRange: 18,
    seekRange: 26,
    preferredRange: 13,
    separationRadius: 2.5,
    separationStrength: 10,
    scoreValue: 30,
    color: PALETTE.enemyRanged,
    weapon: 'spitter',
    ranged: {
      starter_state: 'Idle_Patrol',
      enemy_weapon: 'spitter',
      idle_walk_speed: 2.5,
      aggro_walk_speed: 2.5,
      enemy_detection_cone_range: 26,
      enemy_detection_cone_angle: 110,
      enemy_weapon_shot_seconds: 1.15,
      enemy_aggro_distance_min: 8,
      enemy_aggro_distance_max: 18,
      aggro_default_combat_pattern: 'Strafe_Shooter'
    }
  },

  /**
   * Gunner (issue #57). A grunt-framed foe carrying the enemy Sting: holds mid
   * distance and strafes while spraying. Built from the ranged template — the
   * whole enemy is this data row, no new AI code. `enemy_weapon_shot_seconds`
   * paces the trigger, which is what keeps its infinite ammo fair.
   */
  gunner: {
    name: 'Gunner',
    behavior: 'ranged_template',
    radius: 0.66,
    height: 1.5,
    maxHealth: 10,
    speed: 2.5,
    acceleration: 24,
    damage: 0,
    attackRate: 0,
    // Coherent with smg_enemy's 30-unit range.
    attackRange: 28,
    seekRange: 30,
    preferredRange: 16,
    separationRadius: 2.4,
    separationStrength: 10,
    scoreValue: 28,
    color: PALETTE.enemyGunner,
    weapon: 'smg_enemy',
    ranged: {
      starter_state: 'Idle_Patrol',
      enemy_weapon: 'smg_enemy',
      idle_walk_speed: 2.5,
      aggro_walk_speed: 2.5,
      enemy_detection_cone_range: 30,
      enemy_detection_cone_angle: 110,
      enemy_weapon_shot_seconds: 0.45,
      enemy_aggro_distance_min: 8,
      enemy_aggro_distance_max: 22,
      aggro_default_combat_pattern: 'Strafe_Shooter'
    }
  },

  /**
   * Shotgunner (issue #57). Aggressive brawler with the enemy Splitter: closes
   * the gap and hoses the player at point-blank, so its stand-off ranges are
   * short to match the gun's 15-unit reach — a shotgun kept at distance would
   * never connect.
   */
  shotgunner: {
    name: 'Shotgunner',
    behavior: 'ranged_template',
    radius: 0.7,
    height: 1.55,
    maxHealth: 10,
    speed: 2,
    acceleration: 26,
    damage: 0,
    attackRate: 0,
    attackRange: 14,
    seekRange: 30,
    preferredRange: 8,
    separationRadius: 2.6,
    separationStrength: 10,
    scoreValue: 34,
    color: PALETTE.enemyShotgunner,
    weapon: 'shotgun_enemy',
    ranged: {
      starter_state: 'Idle_Patrol',
      enemy_weapon: 'shotgun_enemy',
      idle_walk_speed: 2,
      aggro_walk_speed: 2,
      enemy_detection_cone_range: 28,
      enemy_detection_cone_angle: 120,
      enemy_weapon_shot_seconds: 1.1,
      enemy_aggro_distance_min: 4,
      enemy_aggro_distance_max: 12,
      aggro_default_combat_pattern: 'Moving_Shooter'
    }
  },

  /**
   * Sniper (issue #57). Fragile long-range shooter with the enemy Lance. Holds
   * far back, and the Shoot_And_Hide pattern makes it break line of sight and
   * reposition once it has fired — "se reposiciona al ser visto". The Lance's
   * slow round (60 u/s) plus the long cadence make each shot dodgeable.
   */
  sniper: {
    name: 'Sniper',
    behavior: 'ranged_template',
    radius: 0.6,
    height: 1.55,
    maxHealth: 10,
    speed: 2,
    acceleration: 22,
    damage: 0,
    attackRate: 0,
    attackRange: 58,
    seekRange: 40,
    preferredRange: 30,
    separationRadius: 2.5,
    separationStrength: 10,
    scoreValue: 40,
    color: PALETTE.enemySniper,
    dropBonus: 0.1,
    weapon: 'railgun_enemy',
    ranged: {
      starter_state: 'Idle_Static',
      enemy_weapon: 'railgun_enemy',
      idle_walk_speed: 2,
      aggro_walk_speed: 2,
      idle_static_scan_angle: 45,
      enemy_detection_cone_range: 58,
      enemy_detection_cone_angle: 90,
      enemy_weapon_shot_seconds: 2.6,
      enemy_aggro_distance_min: 20,
      enemy_aggro_distance_max: 50,
      aggro_default_combat_pattern: 'Shoot_And_Hide',
      aggro_combat_patterns: {
        Shoot_And_Hide: 0.5,
        Cover_Shooter: 0.3,
        Static_Shooter: 0.2
      }
    }
  },

  /**
   * Rocketeer (issue #57). Slow, tanky elite carrying the enemy Comet. Fires the
   * owner-agnostic rocket at a crawl and holds its ground — a rare, high-threat
   * spawn. Its blast chains into other enemies on purpose (see the weapon row),
   * so it can wipe its own crowd if the player kites it into one.
   */
  rocketeer: {
    name: 'Rocketeer',
    behavior: 'ranged_template',
    radius: 0.95,
    height: 2.1,
    maxHealth: 10,
    speed: 1.5,
    acceleration: 14,
    damage: 0,
    attackRate: 0,
    attackRange: 44,
    seekRange: 40,
    preferredRange: 26,
    separationRadius: 3,
    separationStrength: 11,
    scoreValue: 70,
    color: PALETTE.enemyRocketeer,
    dropBonus: 0.3,
    weapon: 'rocket_launcher_enemy',
    ranged: {
      starter_state: 'Idle_Static',
      enemy_weapon: 'rocket_launcher_enemy',
      idle_walk_speed: 1.5,
      aggro_walk_speed: 1.5,
      enemy_detection_cone_range: 44,
      enemy_detection_cone_angle: 100,
      enemy_weapon_shot_seconds: 3.2,
      enemy_aggro_distance_min: 14,
      enemy_aggro_distance_max: 40,
      aggro_default_combat_pattern: 'Static_Shooter',
      aggro_combat_patterns: {
        Static_Shooter: 0.6,
        Moving_Shooter: 0.4
      }
    }
  },

  /**
   * Suicide bomber (issue #5). Idles inside a vision cone until it sees or is
   * hurt by the player, then charges and detonates on contact. Every field
   * below is data — variants ("mine", "kamikaze drone", "creeper") are new rows
   * with different numbers, never new code. The FSM lives in EnemyAISystem.
   */
  suicide_bomber: {
    name: 'Suicide Bomber',
    behavior: 'melee',
    radius: 0.7,
    height: 1.5,
    maxHealth: 10,
    // Idle speed is small; the real motion comes from the charge below.
    speed: 3,
    acceleration: 34,
    // No conventional attack: the whole threat is the blast.
    damage: 0,
    attackRate: 0,
    attackRange: 0,
    seekRange: 0,
    preferredRange: 0,
    separationRadius: 1.8,
    separationStrength: 7,
    scoreValue: 25,
    color: PALETTE.enemySuicideBomber,
    despawnSeconds: 6,

    starterState: 'idle_static',

    detection: {
      range: 26,
      angle: 130,
      checkSeconds: 0.2
    },

    idle: {
      walkSpeed: 3,
      roamingSelectSeconds: 3,
      roamingRange: 6,
      patrolSelectSeconds: 3.5,
      patrolRange: 9,
      patrolMaxDistanceFromSpawn: 0
    },

    aggro: {
      chargeSpeed: 8.5,
      rotationSpeed: 11,
      repathSeconds: 0.35,
      repathMovementThreshold: 2,
      canLoseTarget: false,
      targetMemorySeconds: 3,
      lostTargetReturnState: 'idle_static',
      invalidTargetReturnState: 'idle_static',
      accelerationEnabled: true,
      initialSpeed: 5,
      maxSpeed: 11,
      accelerationPerSecond: 6,
      arrivalTolerance: 0.6,
      stuckDetectionSeconds: 0.6,
      stuckDistanceThreshold: 0.35
    },

    explosiveTrigger: {
      triggerDistance: 1.6,
      fuseSeconds: 0.35,
      deathFuseSeconds: 0,
      explodeOnDeath: true,
      damage: 110,
      radius: 5,
      falloffEnabled: true,
      minimumDamage: 16,
      force: 16,
      requiresLineOfSight: true,
      affectsPlayer: true,
      affectsEnemies: true,
      affectsEnvironment: false
    }
  }
});
